import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, UserPlus, Search, Crown, Shield, Briefcase, Trash2, Loader2, X, FileOutput, History, ChevronDown, AlertTriangle, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const ROLE_META = {
  owner: { label: 'Īpašnieks', icon: Crown, cls: 'bg-amber-500/20 text-amber-300' },
  co_owner: { label: 'Līdzīpašnieks', icon: Shield, cls: 'bg-sky-500/20 text-sky-300' },
  employee: { label: 'Darbinieks', icon: Briefcase, cls: 'bg-emerald-500/20 text-emerald-300' },
};

function RolePill({ role }) {
  const m = ROLE_META[role] || ROLE_META.employee;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${m.cls}`} data-testid={`team-role-pill-${role}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

export default function BusinessTeamSection({ hasPage, pageId, pageLabel }) {
  const [members, setMembers] = useState([]);
  const [myRole, setMyRole] = useState('owner');
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [outgoing, setOutgoing] = useState([]);
  // Page-handover flow
  const [xferMode, setXferMode] = useState(false);
  const [xq, setXq] = useState('');
  const [xresults, setXresults] = useState([]);
  const [xsearching, setXsearching] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [confirmXfer, setConfirmXfer] = useState(null);
  const [moveBalance, setMoveBalance] = useState(false);

  const load = useCallback(async () => {
    if (!pageId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/business-team/members`, { params: { page_id: pageId } });
      setMembers(data.members || []);
      setMyRole(data.my_role || 'owner');
      axios.get(`${API}/business-team/transfers`).then((r) => setTransfers(r.data.transfers || [])).catch(() => {});
      axios.get(`${API}/business-team/page-offers/outgoing`).then((r) => setOutgoing(r.data.offers || [])).catch(() => {});
    } catch (e) { setMembers([]); }
    finally { setLoading(false); }
  }, [pageId]);

  useEffect(() => { if (hasPage && pageId) load(); else setLoading(false); }, [hasPage, pageId, load]);

  const isOwner = myRole === 'owner';
  const canInvite = myRole === 'owner' || myRole === 'co_owner';

  // Invite search
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/business-team/search`, { params: { q: q.trim() } });
        const have = new Set(members.map((m) => m.id));
        setResults((data.results || []).filter((r) => !have.has(r.id)));
      } catch (e) { setResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [q, members]);

  // Transfer-recipient search
  useEffect(() => {
    if (!xferMode || xq.trim().length < 2) { setXresults([]); return undefined; }
    const t = setTimeout(async () => {
      setXsearching(true);
      try {
        const { data } = await axios.get(`${API}/business-team/search`, { params: { q: xq.trim() } });
        setXresults(data.results || []);
      } catch (e) { setXresults([]); }
      finally { setXsearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [xq, xferMode]);

  const invite = async (userId, role) => {
    setBusyId(userId);
    try {
      const { data } = await axios.post(`${API}/business-team/invite`, { user_id: userId, role, page_id: pageId });
      if (data.status === 'member') toast.info('Lietotājs jau ir šīs lapas komandā');
      else toast.success('Uzaicinājums nosūtīts');
      setQ(''); setResults([]); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Neizdevās uzaicināt'); }
    finally { setBusyId(null); }
  };

  const changeRole = async (userId, role) => {
    setBusyId(userId);
    try {
      await axios.put(`${API}/business-team/members/${userId}`, { role, page_id: pageId });
      toast.success('Loma atjaunināta'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Neizdevās mainīt lomu'); }
    finally { setBusyId(null); }
  };

  const remove = async (userId) => {
    setBusyId(userId);
    try {
      await axios.delete(`${API}/business-team/members/${userId}`, { params: { page_id: pageId } });
      toast.success('Dalībnieks noņemts'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Neizdevās noņemt'); }
    finally { setBusyId(null); }
  };

  const openConfirmXfer = async (user) => {
    setPreviewing(true); setMoveBalance(false);
    try {
      const { data } = await axios.get(`${API}/business-team/transfer-page/preview`, { params: { page_id: pageId } });
      setConfirmXfer({ user, preview: data });
    } catch (e) { toast.error(e.response?.data?.detail || 'Neizdevās ielādēt kopsavilkumu'); }
    finally { setPreviewing(false); }
  };

  const sendOffer = async () => {
    if (!confirmXfer) return;
    setBusyId(confirmXfer.user.id);
    try {
      await axios.post(`${API}/business-team/transfer-page`, { user_id: confirmXfer.user.id, page_id: pageId, move_balance: moveBalance });
      toast.success('Nodošanas piedāvājums nosūtīts');
      setConfirmXfer(null); setXferMode(false); setXq(''); setXresults([]); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Neizdevās nosūtīt'); }
    finally { setBusyId(null); }
  };

  if (!hasPage || !pageId) {
    return (
      <div className="mb-8 rounded-2xl border border-white/10 bg-white/5 px-5 py-10 text-center" data-testid="team-section-nopage">
        <Users className="w-7 h-7 mx-auto mb-2 text-white/20" />
        <p className="text-sm text-white/40">Izvēlies uzņēmuma lapu, lai pārvaldītu tās komandu.</p>
      </div>
    );
  }

  return (
    <div className="mb-8 space-y-6" data-testid="team-section">
      <div className="flex items-center gap-2 text-xs text-white/50" data-testid="team-page-scope">
        <Users className="w-3.5 h-3.5 text-[var(--accent)]" /> Komanda lapai: <span className="text-white/80 font-semibold">{pageLabel || 'Lapa'}</span>
      </div>

      {/* Invite */}
      {canInvite && (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="team-invite-panel">
          <div className="flex items-center gap-2 mb-1">
            <UserPlus className="w-4 h-4 text-[var(--accent)]" />
            <p className="font-semibold text-white text-sm">Uzaicināt šīs lapas komandā</p>
          </div>
          <p className="text-xs text-white/50 mb-3">Atrastais lietotājs piekļūs TIKAI šai lapai — ne pārējām tavām lapām.</p>
          <div className="relative">
            <Search className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Meklēt lietotāju pēc vārda…"
              className="w-full h-11 rounded-xl bg-white/5 border border-white/10 text-white text-sm pl-9 pr-9 focus:border-[var(--accent)] outline-none [color-scheme:dark]"
              data-testid="team-search-input" />
            {q && <button onClick={() => { setQ(''); setResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"><X className="w-4 h-4" /></button>}
          </div>
          {(searching || results.length > 0) && (
            <div className="mt-3 rounded-xl border border-white/10 bg-black/20 divide-y divide-white/5 overflow-hidden" data-testid="team-search-results">
              {searching && <p className="px-4 py-3 text-xs text-white/40 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Meklē…</p>}
              {!searching && results.map((r) => (
                <div key={r.id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`team-search-result-${r.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {r.avatar_url ? <img src={r.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                      : <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/60 text-sm font-bold">{(r.name || '?').charAt(0)}</span>}
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{r.name || 'Lietotājs'}</p>
                      {r.region && <p className="text-[11px] text-white/40 capitalize">{r.region}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => invite(r.id, 'employee')} disabled={busyId === r.id}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-500/90 text-white hover:bg-emerald-500 disabled:opacity-50 transition-colors" data-testid={`team-invite-employee-${r.id}`}>
                      {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Darbinieks'}
                    </button>
                    {isOwner && (
                      <button onClick={() => invite(r.id, 'co_owner')} disabled={busyId === r.id}
                        className="text-xs font-semibold px-3 py-1.5 rounded-full bg-sky-500/90 text-white hover:bg-sky-500 disabled:opacity-50 transition-colors" data-testid={`team-invite-coowner-${r.id}`}>
                        Līdzīpašnieks
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {!searching && results.length === 0 && q.trim().length >= 2 && (
                <p className="px-4 py-3 text-xs text-white/40">Nav atrasts neviens lietotājs.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Members */}
      <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden" data-testid="team-members-panel">
        <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
          <Users className="w-4 h-4 text-[var(--accent)]" />
          <p className="font-semibold text-white text-sm">Komanda</p>
          <span className="text-xs text-white/40">· {members.length}</span>
        </div>
        {loading ? (
          <p className="px-5 py-8 text-center text-sm text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></p>
        ) : members.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-white/40">Komandā vēl nav dalībnieku.</p>
        ) : (
          <div className="divide-y divide-white/5">
            {members.map((m) => (
              <div key={m.id} className="px-5 py-3" data-testid={`team-member-${m.id}`}>
               <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  {m.avatar_url ? <img src={m.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                    : <span className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white/60 text-sm font-bold">{(m.name || '?').charAt(0)}</span>}
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate flex items-center gap-2">
                      {m.name || 'Lietotājs'}{m.is_me && <span className="text-[10px] text-white/40">(tu)</span>}
                    </p>
                    <div className="mt-0.5 flex items-center gap-2">
                      <RolePill role={m.role} />
                      {m.status === 'pending' && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/10 text-white/50" data-testid={`team-pending-${m.id}`}>Gaida</span>}
                    </div>
                  </div>
                </div>
                {/* Owner controls — cannot act on the owner row */}
                {isOwner && m.role !== 'owner' && (
                  <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                    <select value={m.role} onChange={(e) => changeRole(m.id, e.target.value)} disabled={busyId === m.id}
                      className="text-xs rounded-lg bg-white/5 border border-white/15 text-white px-2 py-1.5 [color-scheme:dark]" data-testid={`team-role-select-${m.id}`}>
                      <option value="employee" className="bg-[#0F0F0F]">Darbinieks</option>
                      <option value="co_owner" className="bg-[#0F0F0F]">Līdzīpašnieks</option>
                    </select>
                    <button onClick={() => remove(m.id)} disabled={busyId === m.id} title="Noņemt"
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10 text-white/60 hover:bg-red-500/20 hover:text-red-300 transition-colors" data-testid={`team-remove-${m.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                {/* Co-owner may remove employees only */}
                {!isOwner && canInvite && m.role === 'employee' && (
                  <button onClick={() => remove(m.id)} disabled={busyId === m.id} title="Noņemt"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10 text-white/60 hover:bg-red-500/20 hover:text-red-300 transition-colors shrink-0" data-testid={`team-remove-${m.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
               </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hand this page to another KoLV user */}
      {isOwner && (
        <div className="rounded-2xl border border-sky-500/20 bg-sky-500/5 p-5" data-testid="page-handover-panel">
          <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
            <div className="flex items-center gap-2">
              <FileOutput className="w-4 h-4 text-sky-300" />
              <p className="font-semibold text-white text-sm">Nodot šo lapu citam lietotājam</p>
            </div>
            {!xferMode && (
              <button onClick={() => setXferMode(true)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-sky-500/90 text-white hover:bg-sky-500 transition-colors" data-testid="page-handover-start">
                Nodot lapu
              </button>
            )}
          </div>
          <p className="text-xs text-white/50 mb-3">Lapa un visi tās dati pāries izvēlētā lietotāja “Mans bizness” panelī, kad viņš pieņems piedāvājumu.</p>

          {xferMode && (
            <div data-testid="page-handover-search">
              <div className="relative">
                <Search className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={xq} onChange={(e) => setXq(e.target.value)} placeholder="Meklēt saņēmēju pēc vārda…"
                  className="w-full h-11 rounded-xl bg-white/5 border border-white/10 text-white text-sm pl-9 pr-9 focus:border-sky-400 outline-none [color-scheme:dark]" data-testid="page-handover-input" />
                {xq && <button onClick={() => { setXq(''); setXresults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"><X className="w-4 h-4" /></button>}
              </div>
              {(xsearching || xresults.length > 0) && (
                <div className="mt-3 rounded-xl border border-white/10 bg-black/20 divide-y divide-white/5 overflow-hidden" data-testid="page-handover-results">
                  {xsearching && <p className="px-4 py-3 text-xs text-white/40 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Meklē…</p>}
                  {!xsearching && xresults.map((r) => (
                    <div key={r.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`page-handover-result-${r.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        {r.avatar_url ? <img src={r.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                          : <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/60 text-sm font-bold">{(r.name || '?').charAt(0)}</span>}
                        <p className="text-sm text-white truncate">{r.name || 'Lietotājs'}</p>
                      </div>
                      <button onClick={() => openConfirmXfer(r)} disabled={previewing}
                        className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50 transition-colors" data-testid={`page-handover-pick-${r.id}`}>
                        {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileOutput className="w-3.5 h-3.5" />} Nodot
                      </button>
                    </div>
                  ))}
                  {!xsearching && xresults.length === 0 && xq.trim().length >= 2 && (
                    <p className="px-4 py-3 text-xs text-white/40">Nav atrasts neviens lietotājs.</p>
                  )}
                </div>
              )}
              <button onClick={() => { setXferMode(false); setXq(''); setXresults([]); }} className="mt-3 text-xs px-3 py-1.5 rounded-full bg-white/10 text-white/60 hover:bg-white/20" data-testid="page-handover-cancel">Atcelt</button>
            </div>
          )}

          {outgoing.filter((o) => o.page_id === pageId).map((o) => (
            <div key={o.id} className="mt-3 rounded-xl bg-amber-500/10 border border-amber-500/25 px-3 py-2 flex items-center gap-2 text-xs text-amber-200" data-testid={`outgoing-offer-${o.id}`}>
              <Clock className="w-3.5 h-3.5 shrink-0" /> Nodošana gaida — nosūtīts lietotājam <span className="font-semibold">{(o.to_name || '').trim()}</span>.
            </div>
          ))}
        </div>
      )}

      {/* Transfer history */}
      {transfers.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden" data-testid="transfer-history-panel">
          <button onClick={() => setShowHistory((v) => !v)} className="w-full px-5 py-3 flex items-center justify-between gap-2" data-testid="transfer-history-toggle">
            <span className="flex items-center gap-2 font-semibold text-white text-sm"><History className="w-4 h-4 text-[var(--accent)]" /> Nodošanas vēsture <span className="text-xs text-white/40">· {transfers.length}</span></span>
            <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
          </button>
          {showHistory && (
            <div className="divide-y divide-white/5 border-t border-white/10">
              {transfers.map((t) => (
                <div key={t.id} className="px-5 py-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`transfer-row-${t.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm text-white">Lapa “{t.page_label}” → <span className="text-white/85 font-semibold">{t.to_name || 'lietotājs'}</span></p>
                    <p className="text-[11px] text-white/45 mt-0.5">
                      {t.at ? new Date(t.at).toLocaleString('lv-LV') : ''}
                      {t.counts?.bookings ? ` · ${t.counts.bookings} rez.` : ''}
                      {t.balance_moved > 0 ? ` · €${Number(t.balance_moved).toFixed(2)} pārskaitīti` : ''}
                    </p>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/10 text-white/50 shrink-0">Lapa</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Page-transfer confirmation modal */}
      {confirmXfer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={() => (busyId ? null : setConfirmXfer(null))} data-testid="page-transfer-confirm-modal">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#141414] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-5 h-5 text-amber-400" /><p className="text-white font-semibold">Nodot lapu — apstiprini</p></div>
            <p className="text-sm text-white/60 mb-4">Lapa <span className="text-white font-semibold">“{pageLabel}”</span> un visi tās dati <span className="text-white">pilnībā pāries</span> lietotājam <span className="text-white font-semibold">{confirmXfer.user.name}</span>, tiklīdz viņš pieņems piedāvājumu. Lapa pazudīs no tavām lapām.</p>
            <div className="rounded-xl bg-black/25 border border-white/10 p-3 space-y-1.5 mb-4 text-sm" data-testid="page-transfer-summary">
              <div className="flex items-center justify-between"><span className="text-white/55">Rezervācijas / pasūtījumi</span><span className="text-white font-semibold">{confirmXfer.preview.bookings}</span></div>
              <div className="flex items-center justify-between"><span className="text-white/55">Dāvanu kartes</span><span className="text-white font-semibold">{confirmXfer.preview.gift_cards}</span></div>
              {confirmXfer.preview.commission_owed > 0 && (
                <div className="flex items-center justify-between"><span className="text-amber-300/80">Neapmaksātā komisija (parāds)</span><span className="text-amber-300 font-semibold">€{Number(confirmXfer.preview.commission_owed).toFixed(2)}</span></div>
              )}
            </div>
            {confirmXfer.preview.transferable > 0 && (
              <label className="flex items-start gap-2 mb-4 cursor-pointer" data-testid="page-transfer-move-balance">
                <input type="checkbox" checked={moveBalance} onChange={(e) => setMoveBalance(e.target.checked)} className="mt-0.5 w-4 h-4 accent-sky-500" data-testid="page-transfer-move-balance-checkbox" />
                <span className="text-sm text-white/75">Pārskaitīt arī šīs lapas nopelnīto <span className="text-white font-semibold">€{Number(confirmXfer.preview.transferable).toFixed(2)}</span> no mana maciņa jaunajam īpašniekam.</span>
              </label>
            )}
            <div className="flex items-center gap-2">
              <button onClick={sendOffer} disabled={busyId === confirmXfer.user.id}
                className="flex-1 h-11 rounded-xl bg-sky-500 text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="page-transfer-final-confirm">
                {busyId === confirmXfer.user.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileOutput className="w-5 h-5" />} Nosūtīt piedāvājumu
              </button>
              <button onClick={() => setConfirmXfer(null)} disabled={busyId === confirmXfer.user.id} className="px-4 h-11 rounded-xl bg-white/10 text-white/70 hover:bg-white/20" data-testid="page-transfer-final-cancel">Atcelt</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
